
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useTheme } from "next-themes";
import { TextGenerateEffect } from "@/components/ui/text-generate-effect";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useUser } from "@/hooks/use-user";

const welcomeMessages = [
  "Bem-vindo à sua próxima aventura literária!",
  "Liberte sua imaginação. Entre no mundo da narrativa.",
  "Sua jornada criativa começa aqui.",
  "Conecte-se, crie e compartilhe suas histórias.",
  "A comunidade de criadores está esperando por você!",
];

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const { user, isLoading } = useUser();
  
  const { data: pinImages } = useQuery({
    queryKey: ["authPinImages"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("posts")
          .select("image_url")
          .not("user_id", "is", null)
          .order("created_at", { ascending: false })
          .limit(10);
        
        if (error) {
          console.error("Error fetching pin images:", error);
          return [];
        }
        
        return data?.map(pin => pin.image_url) || [];
      } catch (error) {
        console.error("Error in pin images query:", error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [welcomeMessage] = useState(welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)]);

  useEffect(() => {
    if (pinImages && pinImages.length > 0) {
      setBackgroundImage(pinImages[Math.floor(Math.random() * pinImages.length)]);
    }
  }, [pinImages]);

  // Check if user is already logged in
  useEffect(() => {
    if (!isLoading && user) {
      navigate("/");
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state change in Auth page:", event);
        
        if (event === "SIGNED_IN" && session?.user) {
          try {
            // Check if user has profile and is onboarded
            const { data: profile, error } = await supabase
              .from("profiles")
              .select("username, is_onboarded")
              .eq("id", session.user.id)
              .maybeSingle();

            if (error) {
              console.error("Error checking profile:", error);
              toast({
                title: "Erro",
                description: "Ocorreu um erro ao verificar seu perfil. Tente novamente.",
                variant: "destructive",
              });
              return;
            }

            // Store full session in localStorage for persistence
            localStorage.setItem('supabase.auth.token', JSON.stringify(session));

            // If no profile exists or user is not onboarded, go to onboarding
            if (!profile || !profile.username || !profile.is_onboarded) {
              console.log("Redirecting to onboarding - Profile:", profile);
              navigate("/onboarding", { replace: true });
            } else {
              console.log("User is onboarded, redirecting to home");
              navigate("/", { replace: true });
            }
          } catch (error) {
            console.error("Authentication error:", error);
            toast({
              title: "Erro",
              description: "Ocorreu um erro durante a autenticação. Tente novamente.",
              variant: "destructive",
            });
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, toast]);

  const fallbackImage = "https://i.pinimg.com/736x/e3/e3/45/e3e34531613d20bd6efc4890136f9ad8.jpg";

  return (
    <div className="min-h-screen flex flex-col md:flex-row relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 left-4 text-foreground/80 hover:bg-background/10 rounded-full z-10"
        onClick={() => navigate('/')}
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
      
      <div className="relative hidden md:block md:w-1/2 bg-black">
        <img 
          src={backgroundImage || fallbackImage} 
          alt="Arte dos usuários" 
          className="h-full w-full object-cover opacity-80"
          style={{ 
            maxHeight: "100vh", 
            objectPosition: "center",
            objectFit: "cover"
          }}
        />
        <div className="absolute bottom-4 left-4 text-white text-xs opacity-70">
          Arte da Comunidade Ink Pins
        </div>
      </div>
      
      <div className="flex-1 flex flex-col justify-center items-center bg-background p-4 md:p-8">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex flex-col items-center justify-center space-y-3">
            <img 
              src="/lovable-uploads/725d4a82-6ca0-4e39-8b72-3e42aff89638.png" 
              alt="Ink Webtoons Logo" 
              className="h-20 w-20"
            />
            <h2 className="text-2xl font-bold text-foreground">
              Ink Webtoons
            </h2>
            
            <div className="text-center mb-6">
              <TextGenerateEffect
                words={welcomeMessage}
                className="text-lg text-foreground/80"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Entre para continuar sua jornada criativa
              </p>
            </div>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-lg border border-border">
            <div className="space-y-4">
              <div className="space-y-2">
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                  onClick={() => supabase.auth.signInWithOAuth({ provider: 'google' })}
                >
                  Entrar com Google
                </Button>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Ou continue com</span>
                </div>
              </div>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const email = (e.currentTarget.elements.namedItem('email') as HTMLInputElement)?.value;
                const password = (e.currentTarget.elements.namedItem('password') as HTMLInputElement)?.value;
                
                if (!email || !password) {
                  toast({
                    title: "Campos obrigatórios",
                    description: "Por favor, preencha email e senha para continuar.",
                    variant: "destructive"
                  });
                  return;
                }
                
                supabase.auth.signInWithPassword({ email, password })
                  .then(({ error }) => {
                    if (error) {
                      toast({
                        title: "Erro ao entrar",
                        description: error.message,
                        variant: "destructive"
                      });
                    }
                  });
              }}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium">Email</label>
                    <input 
                      id="email" 
                      name="email"
                      type="email" 
                      placeholder="seu@email.com" 
                      className="w-full p-2 border border-border rounded-md bg-background"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="password" className="text-sm font-medium">Senha</label>
                      <Button 
                        variant="link" 
                        className="text-primary px-0 h-auto text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          const email = (document.getElementById('email') as HTMLInputElement)?.value;
                          if (!email) {
                            toast({
                              title: "Campo de email obrigatório",
                              description: "Por favor, preencha o email para recuperar sua senha.",
                              variant: "destructive"
                            });
                            return;
                          }
                          supabase.auth.resetPasswordForEmail(
                            email,
                            { redirectTo: window.location.origin + '/auth?reset=true' }
                          );
                          toast({
                            title: "Email enviado",
                            description: "Verifique seu email para instruções de recuperação de senha."
                          });
                        }}
                      >
                        Esqueceu a senha?
                      </Button>
                    </div>
                    <input 
                      id="password" 
                      name="password"
                      type="password" 
                      className="w-full p-2 border border-border rounded-md bg-background"
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white">
                    Entrar
                  </Button>
                </div>
              </form>
              
              <div className="mt-4">
                <p className="text-sm text-center text-muted-foreground">
                  Não tem uma conta?
                </p>
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => {
                    const email = (document.getElementById('email') as HTMLInputElement)?.value || '';
                    const password = (document.getElementById('password') as HTMLInputElement)?.value || '';
                    
                    if (!email || !password) {
                      toast({
                        title: "Campos obrigatórios",
                        description: "Por favor, preencha email e senha para continuar.",
                        variant: "destructive"
                      });
                      return;
                    }
                    
                    supabase.auth.signUp({ 
                      email, 
                      password,
                      options: {
                        emailRedirectTo: window.location.origin + '/auth?verified=true'
                      }
                    })
                    .then(({ error, data }) => {
                      if (error) {
                        toast({
                          title: "Erro ao criar conta",
                          description: error.message,
                          variant: "destructive"
                        });
                      } else {
                        toast({
                          title: "Conta criada com sucesso",
                          description: "Verifique seu email para confirmar sua conta.",
                        });
                      }
                    });
                  }}
                >
                  Criar conta
                </Button>
              </div>
            </div>
          </div>
          
          <div className="text-center text-xs text-muted-foreground mt-8">
            <p>
              Ao continuar, você concorda com nossos{" "}
              <a href="#" className="text-primary hover:underline">Termos de Serviço</a>{" "}
              e{" "}
              <a href="#" className="text-primary hover:underline">Política de Privacidade</a>.
            </p>
            <p className="mt-2">
              © {new Date().getFullYear()} Ink Webtoons. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
